/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Meal } from '../../API';
import { appSyncGraphQLApi } from '../../app/smorgAppSyncGraphQLApi';

export interface AIGenerationJob {
  aiPrompt: string;
  recipesBoardID: string | null | undefined;
  menuID: string | null | undefined;
  addToTop: boolean;
  recipeJobID: string | null | undefined;
  imageJobID: string | null | undefined;
  meal: Meal | null | undefined;
  imageUrl: string | null | undefined;
}

export interface AIGenerationState {
  aiGenerationJobs: AIGenerationJob[];
}

const initialState: AIGenerationState = {
  aiGenerationJobs: [],
};

export const aiGenerationSlice = createSlice({
  name: 'aiGeneration',
  initialState,
  reducers: {
    aiGenerationJobStarted: (state, action: PayloadAction<AIGenerationJob>) => {
      state.aiGenerationJobs.push(action.payload);
    },
    aiJobMealAvailable: (
      state,
      action: PayloadAction<{ recipeJobID: string; meal: Meal }>,
    ) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const job of state.aiGenerationJobs) {
        if (job.recipeJobID === action.payload.recipeJobID) {
          job.meal = action.payload.meal;
        }
      }
    },
    aiJobImageAvailable: (
      state,
      action: PayloadAction<{ imageJobID: string; imageUrl: string }>,
    ) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const job of state.aiGenerationJobs) {
        if (job.imageJobID === action.payload.imageJobID) {
          job.imageUrl = action.payload.imageUrl;
        }
      }
    },
    aiGenerationJobFinished: (
      state,
      action: PayloadAction<{ recipeJobID: string; imageJobID: string }>,
    ) => {
      state.aiGenerationJobs = state.aiGenerationJobs.filter(
        (job) =>
          job.recipeJobID !== action.payload.recipeJobID &&
          job.imageJobID !== action.payload.imageJobID,
      );
    },
  },
  selectors: {
    // TODO this selector returns a non-memoized object.
    jobOutputSelector: (state, recipeJobID, imageJobID) => {
      const matchingJob = state.aiGenerationJobs.find(
        (job) =>
          job.recipeJobID === recipeJobID && job.imageJobID === imageJobID,
      );
      if (!matchingJob) {
        return {};
      }
      return { meal: matchingJob.meal, imageUrl: matchingJob.imageUrl };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(appSyncGraphQLApi.util.resetApiState, (state) => {
      state.aiGenerationJobs = [];
    });
  },
});

export const {
  aiGenerationJobStarted,
  aiJobMealAvailable,
  aiJobImageAvailable,
  aiGenerationJobFinished,
} = aiGenerationSlice.actions;

export const { jobOutputSelector } = aiGenerationSlice.selectors;

export default aiGenerationSlice.reducer;
